import {trimLoneSurrogates} from 'lib/stringUtilsTs';

import {buildSupplementRegionId} from './annotationRegions';
import type {TextProjection} from './annotationTextProjection';
import type {
  Anchor,
  Annotation,
  AnnotationRange,
  SupplementAnchor,
  TextAnchor,
  TextAnnotation
} from './annotationTypes';

/** Context captured around `exact` for the deferred self-heal search. */
const PREFIX_SUFFIX_LENGTH = 32;

/**
 * Whether a range can become a mark: in bounds and covering real text — a drag
 * over just a collapsed newline is not a mark.
 */
export function isMarkableRange(
  projection: TextProjection,
  {start, end}: AnnotationRange
): boolean {
  if (start < 0 || end > projection.text.length || start >= end) {
    return false;
  }
  return projection.text.slice(start, end).trim() !== '';
}

/** Builds the frozen v1 anchor for a normalized range; null when not markable. */
export function buildAnchor(
  projection: TextProjection,
  {start, end}: AnnotationRange,
  {regionId, lang}: {regionId: string; lang: string | null}
): TextAnchor | null {
  if (!isMarkableRange(projection, {start, end})) {
    return null;
  }
  const exact = projection.text.slice(start, end);
  return {
    v: 1,
    kind: 'text',
    regionId,
    lang,
    start,
    end,
    exact,
    prefix: trimLoneSurrogates(
      projection.text.slice(Math.max(0, start - PREFIX_SUFFIX_LENGTH), start)
    ),
    suffix: trimLoneSurrogates(projection.text.slice(end, end + PREFIX_SUFFIX_LENGTH))
  };
}

/** Builds the frozen v1 anchor marking a whole supplement. */
export function buildSupplementAnchor(supplementId: string): SupplementAnchor {
  return {v: 1, kind: 'supplement', regionId: buildSupplementRegionId(supplementId)};
}

export function isSupplementAnchor(anchor: Anchor): anchor is SupplementAnchor {
  return anchor.kind === 'supplement';
}

/**
 * Whether a mark belongs to the text now on screen. A supplement is one object in
 * every language and always passes; text matches exactly, `null` being the original.
 */
export function isAnchoredInLang(anchor: Anchor, lang: string | null): boolean {
  return isSupplementAnchor(anchor) || anchor.lang === lang;
}

export function isTextAnnotation(annotation: Annotation): annotation is TextAnnotation {
  return !isSupplementAnchor(annotation.anchor);
}

/**
 * Validate-or-drop: resolves iff the normalized text still reads `exact` at
 * `[start, end)` — never approximated. A drop is not a deletion; the anchor
 * resolves again when the DOM returns to its captured shape. Whole-supplement
 * anchors never resolve — they have no text range, so the text machinery
 * (paint, hit-testing, note lines, merge) skips them by construction.
 */
export function resolveAnchor(anchor: Anchor, projection: TextProjection): AnnotationRange | null {
  if (isSupplementAnchor(anchor)) {
    return null;
  }
  const {start, end, exact} = anchor;
  if (start < 0 || end <= start) {
    return null;
  }
  if (projection.text.slice(start, end) !== exact) {
    return null;
  }
  return {start, end};
}

/** Groups marks by each one's own `anchor.regionId`, keeping their order within a region. */
export function groupByRegionId(annotations: readonly Annotation[]): Record<string, Annotation[]> {
  const byRegionId: Record<string, Annotation[]> = {};
  for (const annotation of annotations) {
    (byRegionId[annotation.anchor.regionId] ??= []).push(annotation);
  }
  return byRegionId;
}
